/**
 * WorkerHolidays — read-only view of the org's public-holiday calendar for a
 * year (owner 2026-06-26). Backed by GET /v1/me/holidays (self-scoped). Admins
 * manage the calendar in Attendance → Leave → Public Holidays; workers just
 * see "what days are off this year".
 *
 * Mobile twin: svdy-mobile/app/holidays.tsx (same endpoint).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Chip, CircularProgress, IconButton, Paper, Stack, Typography,
} from '@mui/material';
import { ChevronLeftOutlined, ChevronRightOutlined, EventBusyOutlined } from '@mui/icons-material';
import { meApi } from '../../api';
import type { MeHoliday } from '../../api/me';

const WorkerHolidays: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [year, setYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState<MeHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchYear = useCallback(async (y: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await meApi.holidays.list(y);
      setHolidays(res.holidays ?? []);
    } catch {
      setError(t('worker:holidays.loadFailed'));
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchYear(year); }, [year, fetchYear]);

  // Group by month for a calendar-like read.
  const byMonth = useMemo(() => {
    const m = new Map<number, MeHoliday[]>();
    for (const h of holidays) {
      const mo = new Date(h.date + 'T00:00:00').getMonth();
      const arr = m.get(mo); if (arr) arr.push(h); else m.set(mo, [h]);
    }
    return Array.from(m.entries()).sort((a, b) => a[0] - b[0]);
  }, [holidays]);

  const monthName = (mo: number) =>
    new Date(year, mo, 1).toLocaleDateString(i18n.language, { month: 'long' });
  const dayLabel = (iso: string) =>
    new Date(iso + 'T00:00:00').toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric' });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1.5, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('worker:holidays.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('worker:holidays.subtitle')}</Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => setYear((y) => y - 1)} aria-label={t('worker:holidays.prevYear')}>
            <ChevronLeftOutlined fontSize="small" />
          </IconButton>
          <Typography variant="body1" sx={{ fontWeight: 700, minWidth: 56, textAlign: 'center' }}>{year}</Typography>
          <IconButton size="small" onClick={() => setYear((y) => y + 1)} aria-label={t('worker:holidays.nextYear')}>
            <ChevronRightOutlined fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {!loading && !error && holidays.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>{holidays.length}</strong> {t('worker:holidays.statCount')}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : error ? (
        <Paper elevation={0} sx={{ width: '100%', p: 4, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'error.main' }}>
          <Typography variant="body2" color="error">{error}</Typography>
        </Paper>
      ) : holidays.length === 0 ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <EventBusyOutlined sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">{t('worker:holidays.empty', { year })}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>{t('worker:holidays.emptyHint')}</Typography>
        </Paper>
      ) : (
        <Box
          /* `gridAutoRows: '1fr'` equalises EVERY row, not just the cards inside one row.
             Grid already stretches items within a row, so January (2 holidays) and February
             (1) matched — but December sits alone in the last row with one holiday, so its
             row was shorter and its card visibly smaller than the eleven above it (owner
             2026-09-03: holiday 的卡片高度不一致). One track sizing fixes every row. */
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gridAutoRows: '1fr', gap: 2, width: '100%' }}
        >
          {byMonth.map(([mo, items]) => (
            <Paper key={mo} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                {monthName(mo)}
              </Typography>
              <Stack spacing={1}>
                {items.map((h) => (
                  <Stack key={h.date} direction="row" spacing={1.25} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 64 }}>{dayLabel(h.date)}</Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>{h.name}</Typography>
                    {h.recurring && (
                      <Chip label={t('worker:holidays.recurring')} size="small" variant="outlined"
                        sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />
                    )}
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WorkerHolidays;
